// @ts-nocheck
/*
 * Port of journey-operation/list.js (411 lines). BYTE-IDENTICAL.
 * Server-paginated activity/user-journey list. Filter set:
 *   - date range (min = 30 days back to match backend retention),
 *   - module (populated from filter-options endpoint at mount),
 *   - action,
 *   - user ("Action By" filter; backend returns self + descendant users,
 *     everyone for superadmin; hidden when the list is empty).
 * Export walks pages via `EXPORT_LIMIT=100`, capped at
 * `EXPORT_MAX_ROWS=20000` with a warning toast on hit.
 * PrimeReact Dropdown â†’ react-select (with menuPortal so filters stay
 * above the table). PrimeReact DataTable (lazy) â†’ react-bootstrap Table
 * + manual Pagination (matches CampaignReportPage / AgentActivityListPage
 * swap â€” obd3-gui DataTable is client-side only).
 * v1 `ToastNotification` component dropped â€” react-toastify replaces the
 * error toasts inline (same pattern as MohManagement, NotificationList).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const Pagination: any = ({ className, children, ...props }: any) => (
  <ul className={_cx('pagination', className)} {...props}>
    {children}
  </ul>
);
const _pagItem = (label: string, glyph: any) => ({
  active,
  disabled,
  onClick,
  children,
  ...rest
}: any) => (
  <li
    className={_cx('page-item', active && 'active', disabled && 'disabled')}
    aria-label={label}
    {...rest}
  >
    <button
      type="button"
      className="page-link"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {children ?? glyph}
    </button>
  </li>
);
Pagination.Item = _pagItem('Page', null);
Pagination.First = _pagItem('First', '«');
Pagination.Prev = _pagItem('Previous', '‹');
Pagination.Next = _pagItem('Next', '›');
Pagination.Last = _pagItem('Last', '»');
import Select from 'react-select';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import moment from 'moment';
import { getApiCall } from '@/service/httpCall';
import {
  API_JOURNEY_OPERATION_LIST,
  API_JOURNEY_OPERATION_FILTERS,
} from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import { useNavigate } from 'react-router-dom';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
} from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const PAGE_SIZE = 25;
const WINDOW_DAYS = 30; // matches backend retention window
const EXPORT_LIMIT = 100; // max rows the API returns per page
const EXPORT_MAX_ROWS = 20000; // hard cap to protect the browser/DB

const defaultStart = () =>
  moment()
    .subtract(WINDOW_DAYS - 1, 'days')
    .format('YYYY-MM-DD');
const defaultEnd = () => moment().format('YYYY-MM-DD');
const minStart = () =>
  moment()
    .subtract(WINDOW_DAYS - 1, 'days')
    .format('YYYY-MM-DD');

const fmtDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '-';

const selectStyles: any = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 36,
    height: 36,
    fontSize: 12,
    borderRadius: 6,
    border: '1px solid hsl(var(--border) / 0.6)',
    backgroundColor: 'hsl(var(--card))',
    boxShadow: state.isFocused
      ? 'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9), 0 0 0 3px hsl(var(--ring) / 0.15)'
      : 'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9)',
    '&:hover': { borderColor: 'hsl(var(--border))' },
  }),
  valueContainer: (base: any) => ({ ...base, padding: '0 8px', height: 34 }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0, color: 'hsl(var(--foreground))' }),
  singleValue: (base: any) => ({ ...base, color: 'hsl(var(--foreground))' }),
  placeholder: (base: any) => ({ ...base, color: 'hsl(var(--muted-foreground))' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: 6,
    color: 'hsl(var(--muted-foreground))',
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: 8,
    border: '1px solid hsl(var(--border) / 0.6)',
    backgroundColor: 'hsl(var(--card))',
    color: 'hsl(var(--foreground))',
    boxShadow:
      '10px 10px 24px rgba(163, 177, 198, 0.4), -10px -10px 24px rgba(255, 255, 255, 0.9)',
    overflow: 'hidden',
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 12,
    padding: '8px 12px',
    backgroundColor: state.isSelected
      ? 'hsl(var(--primary) / 0.15)'
      : state.isFocused
        ? 'hsl(var(--primary) / 0.08)'
        : 'transparent',
    color: state.isSelected ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
    cursor: 'pointer',
  }),
};

export default function JourneyOperationList() {
  const navigate = useNavigate();

  const allUsersLabel = getUser()?.role === USER_ROLES.SUPERADMIN ? 'All Users' : 'My Activity';

  // filters
  const [startDate, setStartDate] = usePersistedState('journey-operation/JourneyOperationList:startDate', defaultStart());
  const [endDate, setEndDate] = usePersistedState('journey-operation/JourneyOperationList:endDate', defaultEnd());
  const [moduleName, setModuleName] = usePersistedState<any>('journey-operation/JourneyOperationList:moduleName', null);
  const [action, setAction] = usePersistedState<any>('journey-operation/JourneyOperationList:action', null);
  const [userId, setUserId] = usePersistedState<any>('journey-operation/JourneyOperationList:userId', null);

  // filter options
  const [moduleOptions, setModuleOptions] = useState<any[]>([]);
  const [actionOptions, setActionOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);

  // data
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = usePersistedState('journey-operation/JourneyOperationList:page', 1);

  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [, setShowToast] = useState(false);
  const [, setToastProps] = useState<any>(null);

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  // ---- ACL read permission ----
  useEffect(() => {
    const u = getUser();
    if (u?.acl_settings?.module_operations) {
      try {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(u.acl_settings.module_operations),
        );
      } catch (e) {
        console.log(e);
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const u = getUser();
      if (u?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof u.acl_settings.module_operations === 'string'
              ? parseModuleOperations(u.acl_settings.module_operations)
              : u.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  // ---- load filter dropdown options ----
  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
        });
        const resp: any = await getApiCall(
          `${API_JOURNEY_OPERATION_FILTERS}?${params.toString()}`,
          {},
        );
        setModuleOptions([
          { label: 'All Modules', value: null },
          ...(resp?.modules || []).map((m: any) => ({
            label: String(m).toUpperCase(),
            value: m,
          })),
        ]);
        setActionOptions([
          { label: 'All Actions', value: null },
          ...(resp?.actions || []).map((a: any) => ({
            label: String(a).toUpperCase(),
            value: a,
          })),
        ]);
        setUserOptions([
          { label: allUsersLabel, value: null },
          ...(resp?.users || []).map((u: any) => ({
            label: u.username || `#${u.id}`,
            value: u.id,
          })),
        ]);
      } catch (e) {
        onError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildParams = useCallback(
    (targetPage: number, limit: number, overrides: any = {}) => {
      const f = {
        start_date: startDate,
        end_date: endDate,
        module_name: moduleName,
        action,
        user_id: userId,
        ...overrides,
      };
      const params = new URLSearchParams({
        start_date: f.start_date,
        end_date: f.end_date,
        page: String(targetPage),
        limit: String(limit),
      });
      if (f.module_name) params.append('module_name', f.module_name);
      if (f.action) params.append('action', f.action);
      if (f.user_id) params.append('user_id', String(f.user_id));
      return params;
    },
    [startDate, endDate, moduleName, action, userId],
  );

  const fetchData = useCallback(
    async (targetPage = 1, overrides: any = {}) => {
      const s = overrides.start_date ?? startDate;
      const e = overrides.end_date ?? endDate;
      if (!s || !e) {
        toast.error('Select both start and end dates');
        return;
      }
      if (moment(s).isAfter(moment(e))) {
        toast.error('Start date cannot be after end date');
        return;
      }
      setLoading(true);
      try {
        const params = buildParams(targetPage, PAGE_SIZE, overrides);
        const resp: any = await getApiCall(
          `${API_JOURNEY_OPERATION_LIST}?${params.toString()}`,
          {},
        );
        setRows(Array.isArray(resp?.rows) ? resp.rows : []);
        setTotal(Number(resp?.total) || 0);
        setPage(Number(resp?.page) || targetPage);
      } catch (err) {
        onError(err);
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [buildParams, startDate, endDate],
  );

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => fetchData(1);

  const handleRefresh = () => {
    const reset = {
      start_date: defaultStart(),
      end_date: defaultEnd(),
      module_name: null,
      action: null,
      user_id: null,
    };
    setModuleName(null);
    setAction(null);
    setUserId(null);
    setStartDate(reset.start_date);
    setEndDate(reset.end_date);
    fetchData(1, reset);
  };

  // ---- export: pages the filtered set from the server (capped) ----
  const handleExport = async () => {
    try {
      setButtonDisable(true);
      const collected: any[] = [];
      let p = 1;
      while (true) {
        const params = buildParams(p, EXPORT_LIMIT);
        const resp: any = await getApiCall(
          `${API_JOURNEY_OPERATION_LIST}?${params.toString()}`,
          {},
        );
        const batch = Array.isArray(resp?.rows) ? resp.rows : [];
        collected.push(...batch);
        const grandTotal = Number(resp?.total) || 0;
        if (
          batch.length < EXPORT_LIMIT ||
          collected.length >= grandTotal ||
          collected.length >= EXPORT_MAX_ROWS
        ) {
          break;
        }
        p += 1;
      }

      if (collected.length === 0) {
        toast.error('No data to export for the selected filters');
        return;
      }
      if (collected.length >= EXPORT_MAX_ROWS) {
        toast.warning
          ? toast.warning(
              `Export capped at ${EXPORT_MAX_ROWS} rows. Narrow the filters for a full export.`,
            )
          : toast(`Export capped at ${EXPORT_MAX_ROWS} rows. Narrow the filters for a full export.`);
      }

      exportToCSV({
        fileName: 'activity-list',
        data: collected.map((row, idx) => ({ ...row, key: idx + 1 })),
        columns: [
          { header: '#', accessor: 'key' },
          {
            header: 'Module Name',
            accessor: (row: any) => row.module_name?.toUpperCase(),
          },
          { header: 'Action', accessor: (row: any) => row.action?.toUpperCase() },
          {
            header: 'Action By',
            accessor: (row: any) => row.creator?.username || '-',
          },
          { header: 'Date', accessor: (row: any) => fmtDate(row.created_at) },
        ],
      });
    } catch (err) {
      onError(err);
    } finally {
      setButtonDisable(false);
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'Activity List',
    'read',
  );
  if (Object.keys(moduleOperationsForReadPermissionCheck).length > 0 && !canRead) {
    return (
      <div
        className="text-center p-4"
        style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  const selectedModuleOption =
    moduleOptions.find((o: any) => o.value === moduleName) || moduleOptions[0];
  const selectedActionOption =
    actionOptions.find((o: any) => o.value === action) || actionOptions[0];
  const selectedUserOption =
    userOptions.find((o: any) => o.value === userId) || userOptions[0];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);

  return (
    <FillPage>
      <PageHeader
        title="Activity List"
        description="User-level activity log across modules — filterable by date, module, action, and user."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={buttonDisable || total === 0}
            className="gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            Export
            {buttonDisable && (
              <span
                className="ml-1 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-hidden
              />
            )}
          </Button>
        }
      />

      {/* Filters */}
      <div
        className="shrink-0 rounded-xl border border-border/60 bg-card px-3 py-2.5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <DateRangeFilter
          compact
          value={{ from: startDate, to: endDate }}
          onChange={({ from, to }) => {
            setStartDate(from);
            setEndDate(to);
          }}
          fromLabel="Start Date"
          toLabel="End Date"
          min={minStart()}
          max={defaultEnd()}
          onApply={handleApply}
          applyLabel={loading ? 'Loading…' : 'Apply'}
          onReset={handleRefresh}
          disabled={loading}
        >
          <div className="w-[180px] space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Module
            </label>
            <Select
              value={selectedModuleOption}
              options={moduleOptions}
              onChange={(opt: any) => setModuleName(opt?.value ?? null)}
              placeholder="All Modules"
              isClearable={false}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={selectStyles}
            />
          </div>
          <div className="w-[180px] space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Action
            </label>
            <Select
              value={selectedActionOption}
              options={actionOptions}
              onChange={(opt: any) => setAction(opt?.value ?? null)}
              placeholder="All Actions"
              isClearable={false}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={selectStyles}
            />
          </div>
          {userOptions.length > 1 && (
            <div className="w-[200px] space-y-0.5">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Action By
              </label>
              <Select
                value={selectedUserOption}
                options={userOptions}
                onChange={(opt: any) => setUserId(opt?.value ?? null)}
                placeholder={allUsersLabel}
                isClearable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={selectStyles}
              />
            </div>
          )}
        </DateRangeFilter>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card max-lg:min-h-[420px]">
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_hsl(var(--border))] [&_th]:bg-primary/[0.06]">
                  <tr>
                    <th className="w-12 px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      #
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Module Name
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Action
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Action By
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-10 text-center text-sm text-muted-foreground"
                      >
                        No activity found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r: any, idx: number) => {
                      const isStripe = idx % 2 === 1;
                      return (
                        <tr
                          key={r.id ?? idx}
                          className={
                            'border-t border-border/50 transition-colors hover:bg-primary/[0.06] ' +
                            (isStripe ? 'bg-muted/30' : 'bg-card')
                          }
                        >
                          <td className="px-4 py-2.5 text-xs tabular-nums text-muted-foreground">
                            {(page - 1) * PAGE_SIZE + idx + 1}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
                              {r.module_name?.toUpperCase() || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="inline-flex items-center rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
                              {r.action?.toUpperCase() || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-sm text-foreground">
                            {r.creator?.username || '—'}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                            {fmtDate(r.created_at)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {total > 0 && (
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-1 text-xs text-muted-foreground">
              <span>
                Page{' '}
                <span className="font-medium text-foreground">{page}</span> of{' '}
                <span className="font-medium text-foreground">{totalPages}</span>
                {' · '}
                <span className="font-medium text-foreground">{total}</span> total
              </span>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(1)}
                  disabled={page <= 1}
                  className="gap-1.5"
                  aria-label="First page"
                >
                  <ChevronsLeft className="h-3.5 w-3.5" />
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page - 1)}
                  disabled={page <= 1}
                  className="gap-1.5"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page + 1)}
                  disabled={page >= totalPages}
                  className="gap-1.5"
                  aria-label="Next page"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(totalPages)}
                  disabled={page >= totalPages}
                  className="gap-1.5"
                  aria-label="Last page"
                >
                  Last
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </FillPage>
  );
}
